import { TextTinyLight } from 'base/ui/typography';
import React from 'react';
import { PageState, Project, ProjectState, SectionRef, Space } from '../model';
import { isUserWrittenName } from './page_sections';

// estimated 4 chars per token
export const PROJECT_BRIEF_LIMIT = 1250 * 4;
export const PAGE_NAME_LIMIT = 25 * 4;
export const PAGE_DESCRIPTION_LIMIT = 1250 * 4;
export const SECTION_NAME_LIMIT = 25 * 4;
export const SECTION_DESCRIPTION_LIMIT = 125 * 4;

export function LimitCounter({
  value,
  limit,
}: {
  value: string | undefined;
  limit: number;
}) {
  const count = value?.length || 0;
  if (count < limit * 0.75) return null;
  return (
    <TextTinyLight align="right" color={count > limit ? 'red' : 'grey'}>
      {formatNumber(count)}/{formatNumber(limit)}
    </TextTinyLight>
  );
}

function formatNumber(n: number) {
  if (!Intl.NumberFormat) return n.toString();
  return new Intl.NumberFormat().format(n);
}

export function getCompanyDescriptionError(value: string): string | undefined {
  if (value.length > PROJECT_BRIEF_LIMIT) {
    return 'The sitemap prompt is too long, please shorten it';
  }
  if (!isUserWrittenName(value)) {
    return 'Please write a sitemap prompt';
  }
}

/**
 * Requires that at least one of the following is present:
 *   - Project::brief
 *   - Space::description
 *   - Page::description
 **/
export function getPageContextError(
  page: PageState,
  space: Space | 'home',
  project: ProjectState,
): string | undefined {
  if (page.description.length > PAGE_DESCRIPTION_LIMIT) {
    return 'The page prompt is too long, please shorten it';
  }
  const brief =
    (space === 'home' ? undefined : space.description) || project.brief || '';
  if (brief.length > PROJECT_BRIEF_LIMIT) {
    return 'The prompt is too long, please shorten it';
  }
  if (!isUserWrittenName(brief) && !isUserWrittenName(page.description)) {
    return 'Add a sitemap or page prompt';
  }
}

export function getIsPageDescriptionRequired(
  space: 'home' | Space,
  project: Project,
): boolean {
  const brief =
    (space === 'home' ? undefined : space.description) || project.brief || '';
  return !isUserWrittenName(brief);
}

export function getPageNameError(pageName: string): string | undefined {
  if (!isUserWrittenName(pageName)) {
    return 'Page requires a title to generate';
  }
  if (pageName.length > PAGE_NAME_LIMIT) {
    return 'The page name is too long, please shorten it';
  }
}

export function getPageDescriptionError(
  pageDescription: string | undefined,
): string | undefined {
  if (pageDescription && pageDescription.length > PAGE_DESCRIPTION_LIMIT) {
    return 'The page description is too long, please shorten it';
  }
}

export function getSectionNameError(sectionName: string): string | undefined {
  if (!isUserWrittenName(sectionName)) {
    return 'Please write a short, descriptive section title';
  }
  if (sectionName.length > SECTION_NAME_LIMIT) {
    return 'The section title is too long, please shorten it';
  }
}

export function getSectionDescriptionError(
  description: string,
): string | undefined {
  if (description.length > SECTION_DESCRIPTION_LIMIT) {
    return 'The section description is too long, please shorten it';
  }
}

/**
 * We don't generate copy due to errors with the section, or context for the section (page/sitemap).
 *
 * @returns An object with the following keys:
 *  - contextError: string | undefined - if the page/sitemap is missing descriptions
 * - sectionNameError: string | undefined - if the section name is too long or missing
 * - sectionDescriptionError: string | undefined - if the section description is too long
 */
export function getGenerateCopyError(
  sref: SectionRef,
  page: PageState,
  space: Space | 'home',
  project: ProjectState,
) {
  const section =
    sref.type === 'inline' ? sref.value : project.globalSections.get(sref.id);
  if (!section) return;
  const contextError =
    getPageNameError(page.name) || getPageContextError(page, space, project);
  const sectionNameError = section && getSectionNameError(section.name);
  const sectionDescriptionError =
    section && getSectionDescriptionError(section.description);
  if (!contextError && !sectionNameError && !sectionDescriptionError)
    return null;
  return { contextError, sectionNameError, sectionDescriptionError };
}
